import {equals, filter, find, flow, formatDate, head, pipe, toNumber} from '@yandex-delivery/utils'

import {
  type PaymentContract,
  PaymentContractErrorCode,
  PaymentContractPaymentType,
  PaymentContractStatus,
  PaymentContractType,
} from '../../../model/payment'
import {paymentMethodContractGetter, paymentMethodFinanceGetter, paymentMethodsListGetter} from '../../../repo/cargo'
import type {PaymentMethodContract, PaymentMethodFinance, PaymentMethodsListResponse} from '../../../repo/cargo/types'
import {
  type ContractPaymentType,
  type ContractType,
  type PaymentMethodActualStateResponse,
  type PaymentStatus,
  type ValidationErrorCode,
  paymentMethodActualStateGetter,
} from '../../../repo/dcaa'

// Payment Methods List Getters
const getMethods = paymentMethodsListGetter('methods')

// Payment Method Finance Getters
const getDetailsType = paymentMethodFinanceGetter('details.type')
const getIsSelected = paymentMethodFinanceGetter('is_selected')

// Payment Method Contract Getters
const getId = paymentMethodContractGetter('details.contract_id')
const getInnerId = paymentMethodContractGetter('id')

// Payment Method Actual State Getters
const getType = paymentMethodActualStateGetter('payment_method_info.contract_type')
const getPaymentType = paymentMethodActualStateGetter('payment_method_info.contract_payment_type')
const getThreshold = paymentMethodActualStateGetter('financial_info.deactivate_threshold')
const getPenalty = paymentMethodActualStateGetter('financial_info.penalty')
const getActivationPaymentAmount = paymentMethodActualStateGetter('financial_info.offer_activation_payment_amount')
const getDebtsAmount = paymentMethodActualStateGetter('financial_info.debts_amount')
const getBalance = paymentMethodActualStateGetter('financial_info.balance')
const getCurrencySign = paymentMethodActualStateGetter('financial_info.currency_sign')
const getLastDocumentPaymentStatus = paymentMethodActualStateGetter('last_document_payment_status')
const getLastDocumentPaymentStatusMonth = paymentMethodActualStateGetter('last_document_payment_status.month')
const getLastDocumentPaymentStatusPaymentStatus = paymentMethodActualStateGetter(
  'last_document_payment_status.payment_status',
)
const getLastDocumentPaymentStatusPaymentTermDate = paymentMethodActualStateGetter(
  'last_document_payment_status.payment_term_date',
)
const getMessages = paymentMethodActualStateGetter('validation_error_codes')
const getFirstMessage = flow(getMessages, head)

// Constants
const contractTypeMap: Readonly<Record<ContractType, PaymentContract['type']>> = {
  paper: PaymentContractType.Paper,
  offer: PaymentContractType.Offer,
  unknown: PaymentContractType.Unknown,
}

const errorCodeMap: Readonly<Record<ValidationErrorCode, PaymentContract['errorCode']>> = {
  outstanding_debt: PaymentContractErrorCode.OutstandingDebt,
  inactive_contract: PaymentContractErrorCode.InactiveContract,
  offer_not_accepted: PaymentContractErrorCode.OfferNotAccepted,
  balance_not_found_for_prepaid: PaymentContractErrorCode.BalanceNotFoundForPrepaid,
  insufficient_balance: PaymentContractErrorCode.InsufficientBalance,
  disabled_contract: PaymentContractErrorCode.DisabledContract,
  not_is_registered: PaymentContractErrorCode.NotIsRegistered,
  card_for_direct_debiting_is_missing: PaymentContractErrorCode.CardForDirectDebitingIsMissing,
  contract_is_pending: PaymentContractErrorCode.ContractIsPending,
  overdue_debt: PaymentContractErrorCode.OverdueDebt,
  paper_contract_not_signed: PaymentContractErrorCode.PaperContractNotSigned,
  unknown_error: PaymentContractErrorCode.Unknown,
}

const paymentTypeMap: Readonly<Record<ContractPaymentType, PaymentContract['paymentType']>> = {
  postpaid: PaymentContractPaymentType.Postpaid,
  prepaid: PaymentContractPaymentType.Prepaid,
  unknown: PaymentContractPaymentType.Unknown,
}

const paymentStatusMap: Readonly<Record<PaymentStatus, PaymentContractStatus>> = {
  not_paid: PaymentContractStatus.NotPaid,
  partially_paid: PaymentContractStatus.PartiallyPaid,
  waiting_for_paiment: PaymentContractStatus.WaitingForPayment,
  paid: PaymentContractStatus.Paid,
}

// Mappers
function toErrorCode(errorCode?: ValidationErrorCode): PaymentContractErrorCode | undefined {
  return errorCode ? errorCodeMap[errorCode] : undefined
}

function toType(type: ContractType = 'unknown'): PaymentContractType {
  return contractTypeMap[type] || PaymentContractType.Unknown
}

function toPaymentType(paymentType: ContractPaymentType = 'unknown'): PaymentContractPaymentType {
  return paymentTypeMap[paymentType] || PaymentContractPaymentType.Unknown
}

function toPaymentStatus(paymentStatus?: PaymentStatus): PaymentContractStatus | undefined {
  return paymentStatus ? paymentStatusMap[paymentStatus] : undefined
}

// Predicates
type ContractPaymentGuard = (payload?: PaymentMethodFinance) => payload is PaymentMethodContract
const isContractMethod = flow(getDetailsType, equals('contract')) as ContractPaymentGuard

// @ts-expect-error<TS2322> – не сходятся типы у функций в композиции
const getSelectedContract: (response: PaymentMethodsListResponse) => PaymentMethodContract | undefined = flow(
  getMethods,
  filter(isContractMethod),
  find(getIsSelected),
)

export function toPaymentContract(
  paymentMethodsResponse: PaymentMethodsListResponse,
  actualState: PaymentMethodActualStateResponse,
): PaymentContract {
  const selectedContract = getSelectedContract(paymentMethodsResponse)
  const balance = pipe(actualState, getBalance, toNumber)
  const errorCode = pipe(actualState, getFirstMessage, toErrorCode)
  const threshold = pipe(actualState, getThreshold, toNumber)
  const type = pipe(actualState, getType, toType)
  const paymentType = pipe(actualState, getPaymentType, toPaymentType)
  const penalty = pipe(actualState, getPenalty, toNumber)
  const debtAmount = pipe(actualState, getDebtsAmount, toNumber)
  const activationPaymentAmount = pipe(actualState, getActivationPaymentAmount, toNumber)
  const lastDocumentPaymentStatus = pipe(actualState, getLastDocumentPaymentStatus)
  const status = pipe(actualState, getLastDocumentPaymentStatusPaymentStatus, toPaymentStatus)

  const result: PaymentContract = {
    __type: 'contract',
    id: selectedContract ? getId(selectedContract) : undefined,
    internalID: selectedContract ? getInnerId(selectedContract) : undefined,
    currencySign: getCurrencySign(actualState),
    debtAmount,
    activationPaymentAmount,
    balance,
    errorCode,
    paymentType,
    penalty,
    threshold,
    type,
  }

  if (lastDocumentPaymentStatus) {
    result.lastDocumentPaymentInfo = {
      status,
      month: pipe(actualState, getLastDocumentPaymentStatusMonth, formatDate('YYYY-MM-DD')),
      lastPaymentDate: pipe(actualState, getLastDocumentPaymentStatusPaymentTermDate, formatDate('YYYY-MM-DD')),
    }
  }

  return result
}
